'use client'

import Link from 'next/link'
import { BookOpen, ArrowRight } from 'lucide-react'
import { useInView } from '@/hooks/useInView'

export type RelatedGuide = {
  href: string
  title: string
  description: string
}

export function RelatedGuides({
  heading,
  subheading,
  guides,
  badge = 'Related Guides',
  cta = 'Read guide',
}: {
  heading: string
  subheading: string
  guides: RelatedGuide[]
  badge?: string
  cta?: string
}) {
  const { ref, isInView } = useInView()

  return (
    <section className="section" ref={ref}>
      <div className="section-inner">
        <div className={`text-center mb-12 anim-fade-in-up ${isInView ? 'in-view' : ''}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-600/10 text-red-400 text-xs font-semibold mb-4">
            <BookOpen size={12} />
            {badge}
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
            {heading}
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto">{subheading}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {guides.map((guide, i) => (
            <Link
              key={guide.href}
              href={guide.href}
              className={`glass-card-premium p-6 group flex flex-col anim-fade-in-up anim-delay-${Math.min(i + 1, 8)} ${
                isInView ? 'in-view' : ''
              } hover:-translate-y-0.5 transition-transform duration-200`}
            >
              <h3 className="text-base font-semibold text-white mb-2 group-hover:text-red-400 transition-colors leading-snug">
                {guide.title}
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-4 flex-1">
                {guide.description}
              </p>
              <span className="text-xs text-red-400 font-medium inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                {cta}
                <ArrowRight size={12} />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}
